"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Calendar } from "@/components/ui/calendar";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GetInTouch() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section className="relative min-h-screen bg-background py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {}
      <div className="fixed inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1462798/img/brushed_metal_texture_background.png)] bg-cover bg-center opacity-10 pointer-events-none" />
      
      <div className="relative max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {}
          <div ref={leftRef} className="space-y-8">
            <div className={`transition-all duration-200 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <div className="flex items-start gap-4 p-6 bg-background/80 backdrop-blur-md rounded-lg shadow-soft border border-muted/30 hover-lift">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-lg text-foreground mb-1">Phone</h3>
                  <p className="text-muted-foreground">07508457253</p>
                </div>
              </div>
            </div>

            <div className={`transition-all duration-200 ease-out delay-100 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <div className="flex items-start gap-4 p-6 bg-background/80 backdrop-blur-md rounded-lg shadow-soft border border-muted/30 hover-lift">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-lg text-foreground mb-1">Email</h3>
                  <p className="text-muted-foreground">contact@motorevive.com</p>
                </div>
              </div>
            </div>

            <div className={`transition-all duration-200 ease-out delay-200 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <div className="flex items-start gap-4 p-6 bg-background/80 backdrop-blur-md rounded-lg shadow-soft border border-muted/30 hover-lift">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-lg text-foreground mb-1">Location</h3>
                  <p className="text-muted-foreground">1234 Motorcycle Lane, Rider City, RC 56789</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`transition-all duration-250 ease-in-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-background/80 backdrop-blur-md rounded-lg shadow-medium border border-muted/30 p-8">
              <h2 className="font-display text-3xl font-bold text-foreground mb-2">Book an Appointment</h2>
              <p className="text-muted-foreground mb-6">Schedule your bike restoration service today</p>
              
              <FormProvider formId="appointment-form-contact" sectionName="get-in-touch" defaultValues={{
              name: "",
              email: "",
              phone: "",
              bikeModel: "",
              serviceDate: undefined
            }} validationRules={{
              name: {
                required: true,
                minLength: 2,
                maxLength: 50
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              bikeModel: {
                required: true,
                minLength: 2
              },
              serviceDate: {
                required: true
              }
            }} submitText="Book Appointment" successMessage="Thank you! We'll confirm your appointment shortly.">
                <AppointmentForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function AppointmentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [showCalendar, setShowCalendar] = useState(false);
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-primary/5 border border-primary/20 rounded-lg">
        <p className="text-primary font-medium mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full">
          Book Another Appointment
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" className="transition-all duration-200 focus:ring-2 focus:ring-primary/20 focus:glow" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" className="transition-all duration-200 focus:ring-2 focus:ring-primary/20 focus:glow" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[\d\s\-\+\(\)]+$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className="transition-all duration-200 focus:ring-2 focus:ring-primary/20 focus:glow" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="bikeModel" rules={{
        required: "Bike model is required",
        minLength: {
          value: 2,
          message: "Please provide bike model"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Bike Model</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Honda CBR600" className="transition-all duration-200 focus:ring-2 focus:ring-primary/20 focus:glow" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="serviceDate" rules={{
        required: "Service date is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Preferred Service Date</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input placeholder="Select a date" value={field.value ? new Date(field.value).toLocaleDateString() : ""} onClick={() => setShowCalendar(!showCalendar)} readOnly className="cursor-pointer transition-all duration-200 focus:ring-2 focus:ring-primary/20 focus:glow" />
                  {showCalendar && <div className="absolute top-full left-0 mt-2 z-50 bg-background border border-border rounded-lg shadow-strong p-2">
                      <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => {
                  field.onChange(date);
                  setShowCalendar(false);
                }} disabled={date => date < new Date()} /></SafeHydrate>
                    </div>}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
            <p className="text-destructive text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full transition-all duration-[80ms] hover:scale-[1.02] active:scale-100">
          {isSubmitting ? <>
              <span className="inline-block animate-spin mr-2">⏳</span>
              Booking...
            </> : "Book Appointment"}
        </Button>
      </div>
    </Form>;
}